import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Download, FileText, BarChart3, TrendingUp, 
  Calendar, DollarSign, Syringe, Building2,
  Filter, Loader2, Users, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// Generate years from 2020 to current year + 5
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2020;
  const endYear = currentYear + 5;
  const years = [];
  for (let y = endYear; y >= startYear; y--) {
    years.push(y);
  }
  return years;
};

const YEARS = generateYears();

const ReportsPage = () => {
  const [salesSummary, setSalesSummary] = useState([]);
  const [vaccineCounts, setVaccineCounts] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Enterprise report state
  const [empresas, setEmpresas] = useState([]);
  const [enterpriseReport, setEnterpriseReport] = useState([]);
  const [enterpriseByCompany, setEnterpriseByCompany] = useState([]);
  const [enterpriseLoading, setEnterpriseLoading] = useState(false);
  const [enterpriseSummary, setEnterpriseSummary] = useState({
    total_vacunaciones: 0,
    total_monto: 0,
    total_empresas: 0
  });
  
  const [filters, setFilters] = useState({
    mes: (new Date().getMonth() + 1).toString(),
    anio: new Date().getFullYear().toString()
  });

  // Tipo de vacunación filter state
  const [tiposVacunacion, setTiposVacunacion] = useState([]);
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [tipoReport, setTipoReport] = useState([]);
  const [tipoSummary, setTipoSummary] = useState({ total: 0, total_monto: 0, pendientes: 0, vacunados: 0 });
  const [tipoLoading, setTipoLoading] = useState(false);

  const [enterpriseFilters, setEnterpriseFilters] = useState({
    periodo: 'mes', // dia, semana, mes
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
    empresa_id: 'todas'
  });

  const months = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [salesRes, vaccineRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/reports/sales-summary?mes=${filters.mes}&anio=${filters.anio}`, {
          withCredentials: true
        }),
        axios.get(`${API}/reports/vaccine-counts?mes=${filters.mes}&anio=${filters.anio}`, {
          withCredentials: true
        }),
        axios.get(`${API}/reports/monthly-summary?anio=${filters.anio}`, {
          withCredentials: true
        })
      ]);

      setSalesSummary(salesRes.data);
      setVaccineCounts(vaccineRes.data);
      setMonthlySummary(monthlyRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  }, [filters.mes, filters.anio]);

  const fetchEmpresas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/empresas`, { withCredentials: true });
      setEmpresas(response.data.filter(e => e.active !== false));
    } catch (error) {
      console.error('Error fetching empresas:', error);
    }
  }, []);

  const fetchEnterpriseReport = useCallback(async () => {
    setEnterpriseLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_inicio', enterpriseFilters.fecha_inicio);
      params.append('fecha_fin', enterpriseFilters.fecha_fin);
      if (enterpriseFilters.empresa_id !== 'todas') {
        params.append('empresa_id', enterpriseFilters.empresa_id);
      }

      const response = await axios.get(`${API}/reports/enterprise-vaccinations?${params}`, {
        withCredentials: true
      });
      
      setEnterpriseReport(response.data.vacunaciones || []);
      setEnterpriseByCompany(response.data.por_empresa || []);
      setEnterpriseSummary(response.data.summary || {
        total_vacunaciones: 0,
        total_monto: 0,
        total_empresas: 0
      });
    } catch (error) {
      console.error('Error fetching enterprise report:', error);
      toast.error('Error al cargar el reporte de empresas');
    } finally {
      setEnterpriseLoading(false);
    }
  }, [enterpriseFilters]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  useEffect(() => {
    const fetchTipos = async () => {
      try {
        const res = await axios.get(`${API}/catalogs?category=tipo_vacunacion`, { withCredentials: true });
        setTiposVacunacion(res.data.map(t => t.value).filter(Boolean));
      } catch (error) {
        console.error('Error fetching tipos de vacunación:', error);
      }
    };
    fetchTipos();
  }, []);

  useEffect(() => {
    if (tipoFilter === 'todos') {
      setTipoReport([]);
      setTipoSummary({ total: 0, total_monto: 0, pendientes: 0, vacunados: 0 });
      return;
    }
    const fetchTipoReport = async () => {
      setTipoLoading(true);
      try {
        const params = new URLSearchParams({
          tipo_vacunacion: tipoFilter,
          mes: filters.mes,
          anio: filters.anio
        });
        const res = await axios.get(`${API}/reports/by-tipo-vacunacion?${params}`, { withCredentials: true });
        setTipoReport(res.data.vacunaciones || []);
        setTipoSummary(res.data.summary || { total: 0, total_monto: 0, pendientes: 0, vacunados: 0 });
      } catch (error) {
        console.error('Error fetching tipo report:', error);
        toast.error('Error al cargar el reporte por tipo de vacunación');
      } finally {
        setTipoLoading(false);
      }
    };
    fetchTipoReport();
  }, [tipoFilter, filters.mes, filters.anio]);

  // Set date range based on period selection
  const handlePeriodChange = (periodo) => {
    const today = new Date();
    let fecha_inicio, fecha_fin;

    switch (periodo) {
      case 'dia':
        fecha_inicio = today.toISOString().split('T')[0];
        fecha_fin = today.toISOString().split('T')[0];
        break;
      case 'semana':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1); // Monday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday
        fecha_inicio = weekStart.toISOString().split('T')[0];
        fecha_fin = weekEnd.toISOString().split('T')[0];
        break;
      case 'mes':
        fecha_inicio = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        fecha_fin = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case 'custom':
        // Keep current dates for custom
        return setEnterpriseFilters(prev => ({ ...prev, periodo }));
      default:
        fecha_inicio = today.toISOString().split('T')[0];
        fecha_fin = today.toISOString().split('T')[0];
    }

    setEnterpriseFilters(prev => ({
      ...prev,
      periodo,
      fecha_inicio,
      fecha_fin
    }));
  };

  const handleExportSalesExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/export/sales/excel?mes=${filters.mes}&anio=${filters.anio}`,
        { withCredentials: true, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ventas_${filters.anio}_${filters.mes}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte de ventas exportado');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar');
    }
  };

  const handleExportDetalleExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/export/vaccinations/excel?mes=${filters.mes}&anio=${filters.anio}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `detalle_vacunaciones_${filters.anio}_${filters.mes}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Detalle de vacunaciones exportado');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(error.response?.status === 404 ? 'No hay datos para exportar en este período' : 'Error al exportar');
    }
  };

  const handleExportEnterpriseExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.append('fecha_inicio', enterpriseFilters.fecha_inicio);
      params.append('fecha_fin', enterpriseFilters.fecha_fin);
      if (enterpriseFilters.empresa_id !== 'todas') {
        params.append('empresa_id', enterpriseFilters.empresa_id);
      }

      const response = await axios.get(
        `${API}/export/enterprise-vaccinations/excel?${params}`,
        { withCredentials: true, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const empresaNombre = enterpriseFilters.empresa_id === 'todas' 
        ? 'todas' 
        : empresas.find(e => e.id === enterpriseFilters.empresa_id)?.nombre || 'empresa';
      link.setAttribute('download', `vacunaciones_empresas_${empresaNombre}_${enterpriseFilters.fecha_inicio}_${enterpriseFilters.fecha_fin}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte de empresas exportado');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar');
    }
  };

  const downloadPdf = async (url, filename) => {
    try {
      const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  const handlePdfDiario = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadPdf(`${API}/reports/pdf/diario?fecha=${today}`, `reporte_diario_${today}.pdf`);
  };

  const handlePdfMensual = () => {
    downloadPdf(
      `${API}/reports/pdf/mensual?anio=${filters.anio}&mes=${filters.mes}`,
      `reporte_mensual_${filters.anio}_${filters.mes}.pdf`
    );
  };

  const handlePdfEmpresa = () => {
    if (enterpriseFilters.empresa_id === 'todas') {
      toast.error('Selecciona una empresa específica para generar el PDF');
      return;
    }
    const empresa = empresas.find(e => e.id === enterpriseFilters.empresa_id);
    if (!empresa) return;
    const params = new URLSearchParams({
      empresa: empresa.nombre,
      fecha_desde: enterpriseFilters.fecha_inicio,
      fecha_hasta: enterpriseFilters.fecha_fin,
    });
    downloadPdf(
      `${API}/reports/pdf/empresa?${params}`,
      `reporte_empresa_${empresa.nombre}.pdf`
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    try {
      return new Date(dateStr).toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const totalSales = salesSummary.reduce((sum, item) => sum + (item.total || 0), 0);
  const totalVaccinations = vaccineCounts.reduce((sum, item) => sum + (item.cantidad || 0), 0);
  const uniqueDays = [...new Set(salesSummary.map(s => s.fecha))].length;

  // Prepare data for monthly chart
  const monthlyData = months.map((month, index) => {
    const monthData = monthlySummary.find(m => m.mes === index + 1);
    return {
      mes: month.label.substring(0, 3),
      vacunaciones: monthData?.total_vacunas || 0,
      ventas: monthData?.total_monto || 0
    };
  });

  // Prepare data for vaccine pie chart
  const vaccineChartData = vaccineCounts.slice(0, 6).map((v, i) => ({
    name: v.vacuna,
    value: v.cantidad,
    color: COLORS[i % COLORS.length]
  }));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reportes</h1>
          <p className="text-slate-500">Estadísticas y reportes de vacunaciones</p>
        </div>

        <Tabs defaultValue="general" className="space-y-4">
          <TabsList>
            <TabsTrigger value="general" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="empresas" className="gap-2">
              <Building2 className="w-4 h-4" />
              Empresas
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <Select 
                  value={filters.mes} 
                  onValueChange={(value) => setFilters({...filters, mes: value})}
                >
                  <SelectTrigger className="w-[140px]" data-testid="filter-month">
                    <SelectValue placeholder="Mes" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.anio} 
                  onValueChange={(value) => setFilters({...filters, anio: value})}
                >
                  <SelectTrigger className="w-[100px]" data-testid="filter-year">
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEARS.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  onClick={handleExportSalesExcel}
                  className="gap-2"
                  data-testid="export-sales-btn"
                >
                  <Download className="w-4 h-4" />
                  Exportar
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportDetalleExcel}
                  className="gap-2"
                  data-testid="export-detalle-btn"
                >
                  <Download className="w-4 h-4" />
                  Exportar Detalle
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePdfMensual}
                  className="gap-2"
                  data-testid="pdf-mensual-btn"
                >
                  <FileText className="w-4 h-4" />
                  PDF Mensual
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePdfDiario}
                  className="gap-2"
                  data-testid="pdf-diario-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                  PDF Hoy
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="metric-card" data-testid="total-sales-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Total Ventas del Mes
                    </p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                      {formatCurrency(totalSales)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </Card>

              <Card className="metric-card border-l-emerald-500" data-testid="total-vaccinations-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Total Vacunaciones
                    </p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                      {totalVaccinations}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Syringe className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="metric-card border-l-amber-500" data-testid="days-count-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Días con Actividad
                    </p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                      {uniqueDays}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="w-5 h-5" />
                    Vacunaciones Mensuales {filters.anio}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="vacunaciones" fill="#3B82F6" name="Vacunaciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Vaccine Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Syringe className="w-5 h-5" />
                    Distribución por Vacuna
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={vaccineChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {vaccineChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Vaccine Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5" />
                  Detalle por Vacuna - {months.find(m => m.value === filters.mes)?.label} {filters.anio}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vacuna</TableHead>
                        <TableHead className="text-right">Cantidad</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vaccineCounts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} className="text-center py-8 text-slate-500">
                            No hay datos para el período seleccionado
                          </TableCell>
                        </TableRow>
                      ) : (
                        vaccineCounts.map((item, index) => (
                          <TableRow key={item.vacuna || `vacuna-${index}`}>
                            <TableCell className="font-medium">{item.vacuna}</TableCell>
                            <TableCell className="text-right">{item.cantidad}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
            {/* Filtro por Tipo de Vacunación */}
            <Card data-testid="tipo-vacunacion-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Filtro por Tipo de Vacunación
                </CardTitle>
                <CardDescription>
                  Selecciona un tipo para ver quiénes lo tienen registrado en {months.find(m => m.value === filters.mes)?.label} {filters.anio}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-md">
                  <Select value={tipoFilter} onValueChange={setTipoFilter}>
                    <SelectTrigger data-testid="filter-tipo-vacunacion">
                      <SelectValue placeholder="Tipo de Vacunación" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Seleccionar tipo...</SelectItem>
                      {tiposVacunacion.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {tipoFilter !== 'todos' && (
                  <>
                    <div className="flex flex-wrap gap-3">
                      <Badge variant="secondary" data-testid="tipo-total-badge">
                        Total: {tipoSummary.total}
                      </Badge>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="tipo-vacunados-badge">
                        Vacunados: {tipoSummary.vacunados}
                      </Badge>
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="tipo-pendientes-badge">
                        Pendientes: {tipoSummary.pendientes}
                      </Badge>
                      <Badge variant="outline" className="font-mono">
                        {formatCurrency(tipoSummary.total_monto)}
                      </Badge>
                    </div>

                    {tipoLoading ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                      </div>
                    ) : tipoReport.length === 0 ? (
                      <div className="text-center py-8 text-slate-500" data-testid="tipo-empty-state">
                        No hay vacunaciones de tipo "{tipoFilter}" en este período
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table data-testid="tipo-vacunacion-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Fecha</TableHead>
                              <TableHead>Paciente</TableHead>
                              <TableHead>RUT</TableHead>
                              <TableHead>Vacuna</TableHead>
                              <TableHead>Dosis</TableHead>
                              <TableHead>Estado</TableHead>
                              <TableHead className="text-right">Monto</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tipoReport.map((item, index) => (
                              <TableRow key={item.id || `tipo-${index}`}>
                                <TableCell>{formatDate(item.fecha_vacunacion)}</TableCell>
                                <TableCell className="font-medium">{item.nombre_completo}</TableCell>
                                <TableCell className="font-mono text-sm">{item.rut}</TableCell>
                                <TableCell>{item.vacuna}</TableCell>
                                <TableCell>{item.dosis}</TableCell>
                                <TableCell>
                                  {item.estado === 'VACUNADO' ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Vacunado</Badge>
                                  ) : (
                                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendiente</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {formatCurrency(item.monto || 0)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enterprise Tab */}
          <TabsContent value="empresas" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros de Reporte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Period */}
                  <div className="space-y-2">
                    <Label>Período</Label>
                    <Select 
                      value={enterpriseFilters.periodo} 
                      onValueChange={handlePeriodChange}
                    >
                      <SelectTrigger data-testid="enterprise-period">
                        <SelectValue placeholder="Período" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dia">Hoy</SelectItem>
                        <SelectItem value="semana">Esta Semana</SelectItem>
                        <SelectItem value="mes">Este Mes</SelectItem>
                        <SelectItem value="custom">Personalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Start Date */}
                  <div className="space-y-2">
                    <Label>Fecha Inicio</Label>
                    <Input
                      type="date"
                      value={enterpriseFilters.fecha_inicio}
                      onChange={(e) => setEnterpriseFilters(prev => ({ ...prev, fecha_inicio: e.target.value, periodo: 'custom' }))}
                      data-testid="enterprise-date-start"
                    />
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <Label>Fecha Fin</Label>
                    <Input
                      type="date"
                      value={enterpriseFilters.fecha_fin}
                      onChange={(e) => setEnterpriseFilters(prev => ({ ...prev, fecha_fin: e.target.value, periodo: 'custom' }))}
                      data-testid="enterprise-date-end"
                    />
                  </div>

                  {/* Company */}
                  <div className="space-y-2">
                    <Label>Empresa</Label>
                    <Select 
                      value={enterpriseFilters.empresa_id} 
                      onValueChange={(value) => setEnterpriseFilters(prev => ({ ...prev, empresa_id: value }))}
                    >
                      <SelectTrigger data-testid="enterprise-company">
                        <SelectValue placeholder="Seleccionar empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todas">Todas las Empresas</SelectItem>
                        {empresas.map(empresa => (
                          <SelectItem key={empresa.id} value={empresa.id}>
                            {empresa.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={fetchEnterpriseReport} data-testid="enterprise-search-btn">
                    <Filter className="w-4 h-4 mr-2" />
                    Buscar
                  </Button>
                  <Button variant="outline" onClick={handleExportEnterpriseExcel} data-testid="enterprise-export-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar Excel
                  </Button>
                  <Button variant="outline" onClick={handlePdfEmpresa} data-testid="enterprise-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Total Vacunaciones
                      </p>
                      <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                        {enterpriseSummary.total_vacunaciones || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Syringe className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Total Monto
                      </p>
                      <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                        {formatCurrency(enterpriseSummary.total_monto || 0)}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Empresas Atendidas
                      </p>
                      <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                        {enterpriseSummary.total_empresas || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Resumen por Empresa */}
            {enterpriseByCompany.length > 0 && (
              <Card data-testid="resumen-empresas-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Resumen por Empresa
                  </CardTitle>
                  <CardDescription>Total de vacunados y monto por empresa para facturación</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table data-testid="resumen-empresas-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Empresa</TableHead>
                          <TableHead className="text-right">Vacunados</TableHead>
                          <TableHead className="text-right">Monto Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enterpriseByCompany.map((item, index) => (
                          <TableRow key={item.empresa_nombre || `emp-${index}`}>
                            <TableCell className="font-medium">{item.empresa_nombre}</TableCell>
                            <TableCell className="text-right">{item.cantidad}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.total_monto || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Vacunaciones por Empresa
                </CardTitle>
                <CardDescription>
                  {enterpriseFilters.fecha_inicio} al {enterpriseFilters.fecha_fin}
                  {enterpriseFilters.empresa_id !== 'todas' && (
                    <Badge className="ml-2" variant="secondary">
                      {empresas.find(e => e.id === enterpriseFilters.empresa_id)?.nombre}
                    </Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {enterpriseLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                    <p className="text-slate-500 mt-2">Cargando reporte...</p>
                  </div>
                ) : enterpriseReport.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No hay vacunaciones de empresas en este período</p>
                    <p className="text-sm">Seleccione un rango de fechas y haga clic en "Buscar"</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Empresa</TableHead>
                          <TableHead>Paciente</TableHead>
                          <TableHead>RUT</TableHead>
                          <TableHead>Vacuna</TableHead>
                          <TableHead>Dosis</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enterpriseReport.map((item, index) => (
                          <TableRow key={item.id || item.vacunacion_id || `enterprise-${index}`}>
                            <TableCell>{formatDate(item.fecha)}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.empresa_nombre || '-'}</Badge>
                            </TableCell>
                            <TableCell className="font-medium">{item.paciente_nombre}</TableCell>
                            <TableCell className="font-mono text-sm">{item.paciente_rut}</TableCell>
                            <TableCell>{item.vacuna}</TableCell>
                            <TableCell>{item.dosis}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.precio || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ReportsPage;
